import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isRTLLanguage, LanguageDirection } from '../i18n/languages';

interface DirectionContextType {
  dir: LanguageDirection;
  isRTL: boolean;
  language: string;
}

const DirectionContext = createContext<DirectionContextType>({
  dir: 'ltr',
  isRTL: false,
  language: 'en',
});

interface DirectionProviderProps {
  children: ReactNode;
}

export function DirectionProvider({ children }: DirectionProviderProps) {
  const { i18n } = useTranslation();
  const [dir, setDir] = useState<LanguageDirection>('ltr');

  useEffect(() => {
    const newDir = isRTLLanguage(i18n.language) ? 'rtl' : 'ltr';
    setDir(newDir);

    // Update document attributes
    document.documentElement.dir = newDir;
    document.documentElement.lang = i18n.language;

    // Update body class for CSS targeting
    document.body.classList.remove('ltr', 'rtl');
    document.body.classList.add(newDir);
  }, [i18n.language]);

  return (
    <DirectionContext.Provider
      value={{
        dir,
        isRTL: dir === 'rtl',
        language: i18n.language,
      }}
    >
      {children}
    </DirectionContext.Provider>
  );
}

export function useDirection() {
  const context = useContext(DirectionContext);
  if (!context) {
    throw new Error('useDirection must be used within a DirectionProvider');
  }
  return context;
}

// Hook for getting directional CSS classes
export function useDirectionalClasses() {
  const { isRTL } = useDirection();

  return {
    // Text alignment
    textStart: isRTL ? 'text-right' : 'text-left',
    textEnd: isRTL ? 'text-left' : 'text-right',

    // Margins
    ms: (value: string) => isRTL ? `mr-${value}` : `ml-${value}`,
    me: (value: string) => isRTL ? `ml-${value}` : `mr-${value}`,

    // Padding
    ps: (value: string) => isRTL ? `pr-${value}` : `pl-${value}`,
    pe: (value: string) => isRTL ? `pl-${value}` : `pr-${value}`,

    // Borders
    borderStart: isRTL ? 'border-r' : 'border-l',
    borderEnd: isRTL ? 'border-l' : 'border-r',

    // Positioning
    start: isRTL ? 'right' : 'left',
    end: isRTL ? 'left' : 'right',
    insetStart: (value: string) => isRTL ? `right-${value}` : `left-${value}`,
    insetEnd: (value: string) => isRTL ? `left-${value}` : `right-${value}`,

    // Flex direction
    flexRowReverse: isRTL ? 'flex-row' : 'flex-row-reverse',

    // Transform for icons that need flipping
    flipIcon: isRTL ? 'scale-x-[-1]' : '',

    // Logical property classes
    isRTL,
  };
}

export default DirectionContext;
